import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CalendarDays, Clock, ChevronRight, BookOpen, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getAllArticles, getArticleBySlug } from "@/data/blogArticles";

const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

export default function BlogArticle() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const article = getArticleBySlug(slug);
  const allArticles = getAllArticles();

  useEffect(() => {
    window.scrollTo(0, 0);
    if (article) {
      document.title = article.metaTitle;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute("content", article.metaDescription);
    }
  }, [slug, article]);

  if (!article) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4">
        <div className="text-center">
          <h1 className="font-['Manrope'] text-2xl font-bold text-slate-900 mb-4">Artikel nicht gefunden</h1>
          <Link to="/blog" className="text-cyan-700 hover:underline">Zurück zum Ratgeber</Link>
        </div>
      </div>
    );
  }

  const relatedArticles = allArticles.filter(a => a.slug !== slug).slice(0, 4);

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="blog-article">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-xl border-b border-slate-200/50 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="h-9 w-9 shrink-0 overflow-hidden">
              <img src={ICON_URL} alt="Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" />
            </div>
            <div>
              <span className="text-sm font-bold text-slate-800 font-['Manrope'] block leading-tight">Lehnen & Weinacht</span>
              <span className="text-[9px] text-cyan-600 font-semibold tracking-wider uppercase">Pflegeberatung & Alltagsbegleitung</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/blog" className="text-sm text-slate-500 hover:text-cyan-700 transition-colors hidden sm:inline">Ratgeber</Link>
            <a href="/#termin-buchen" className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-4 py-2 text-sm font-semibold transition-all active:scale-95">
              Termin buchen
            </a>
          </div>
        </div>
      </header>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-slate-400 mb-8" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-cyan-700 transition-colors">Startseite</Link>
          <ChevronRight className="h-3 w-3" />
          <Link to="/blog" className="hover:text-cyan-700 transition-colors">Ratgeber</Link>
          <ChevronRight className="h-3 w-3" />
          <span className="text-slate-600 truncate">{article.title}</span>
        </nav>

        {/* Category + Reading time */}
        <div className="flex items-center gap-3 mb-5">
          <span className="text-xs font-semibold text-cyan-700 bg-cyan-50 px-3 py-1.5 rounded-full">{article.category}</span>
          <span className="flex items-center gap-1 text-xs text-slate-400">
            <Clock className="h-3 w-3" /> {article.readTime}
          </span>
        </div>

        {/* Title */}
        <h1 className="font-['Manrope'] text-3xl sm:text-4xl lg:text-[2.75rem] font-extrabold tracking-tight text-slate-900 leading-tight mb-6">
          {article.title}
        </h1>

        {/* Intro */}
        <p className="text-lg text-slate-600 leading-relaxed mb-10 border-l-4 border-cyan-200 pl-5">
          {article.intro}
        </p>

        {/* Table of contents */}
        {article.sections && article.sections.length > 3 && (
          <div className="bg-white rounded-2xl border border-slate-200/60 p-6 mb-10" data-testid="toc">
            <h2 className="font-['Manrope'] text-sm font-bold text-slate-800 uppercase tracking-wider mb-3">Inhaltsverzeichnis</h2>
            <ul className="space-y-2">
              {article.sections.map((s, i) => (
                <li key={i}>
                  <a href={`#section-${i}`} className="text-sm text-cyan-700 hover:text-cyan-800 hover:underline transition-colors">
                    {s.heading}
                  </a>
                </li>
              ))}
              {article.faq && <li><a href="#faq" className="text-sm text-cyan-700 hover:underline">Häufige Fragen</a></li>}
            </ul>
          </div>
        )}

        {/* Content sections */}
        <div className="prose-custom space-y-10">
          {article.sections.map((section, i) => (
            <section key={i} id={`section-${i}`}>
              <h2 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-4">
                {section.heading}
              </h2>
              {section.paragraphs && section.paragraphs.map((p, j) => (
                <p key={j} className="text-base text-slate-600 leading-relaxed mb-4">{p}</p>
              ))}
              {section.bullets && (
                <ul className="space-y-2 mb-4 ml-1">
                  {section.bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2 text-base text-slate-600">
                      <span className="text-cyan-600 font-bold mt-1 shrink-0">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {section.highlight && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 my-5">
                  <p className="text-sm text-amber-800 font-medium">{section.highlight}</p>
                </div>
              )}
              {section.example && (
                <div className="bg-slate-50 border border-slate-200 rounded-xl p-5 my-5">
                  <p className="text-sm font-semibold text-slate-700 mb-1">{section.example.title}</p>
                  <p className="text-sm text-slate-600">{section.example.text}</p>
                </div>
              )}
            </section>
          ))}
        </div>

        {/* FAQ */}
        {article.faq && article.faq.length > 0 && (
          <section id="faq" className="mt-12 mb-10">
            <h2 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-6">Häufige Fragen</h2>
            <Accordion type="single" collapsible className="space-y-2">
              {article.faq.map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="bg-white border border-slate-200/60 rounded-xl px-5 shadow-sm">
                  <AccordionTrigger className="text-base font-semibold text-slate-800 hover:text-cyan-700 hover:no-underline py-4">
                    {item.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-base text-slate-600 leading-relaxed pb-4">
                    {item.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </section>
        )}

        {/* Legal notice */}
        <div className="bg-slate-100 rounded-xl p-5 mb-10 text-sm text-slate-500 italic">
          Dieser Beitrag dient der allgemeinen Information und ersetzt keine individuelle Pflegeberatung. Da sich gesetzliche Regelungen ändern können, prüfen wir im Beratungsgespräch immer die aktuelle Situation.
        </div>

        {/* CTA Box */}
        <div className="bg-gradient-to-br from-cyan-50 to-green-50 rounded-2xl border border-cyan-100 p-8 mb-12" data-testid="article-cta">
          <h3 className="font-['Manrope'] text-xl sm:text-2xl font-bold text-slate-900 mb-3">
            {article.ctaTitle || "Fragen zu Ihren Pflegeleistungen?"}
          </h3>
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            {article.ctaText || "Unsere digitale Pflegeberatung unterstützt Pflegebedürftige und Angehörige dabei, Pflegeleistungen verständlich einzuordnen. Wir helfen bei Fragen zu Pflegegrad, Entlastungsbetrag, Beratungsbesuch nach \u00a7 37 Abs. 3 SGB XI, Alltagshilfen und zukünftigen Reformen."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href="/#termin-buchen" className="inline-flex items-center justify-center gap-2 bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]">
              <CalendarDays className="h-5 w-5" /> Kostenlose Beratung anfragen
            </a>
            <a href="tel:+491637917044" className="inline-flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-3.5 text-base font-semibold transition-all active:scale-[0.98]">
              <Phone className="h-5 w-5" /> 0163 791 7044
            </a>
          </div>
        </div>

        {/* Internal links to related articles */}
        {article.relatedSlugs && article.relatedSlugs.length > 0 && (
          <section className="mb-12">
            <h3 className="font-['Manrope'] text-lg font-bold text-slate-800 mb-4">Weiterführende Artikel</h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {article.relatedSlugs.map((rs) => {
                const related = getArticleBySlug(rs);
                if (!related) return null;
                return (
                  <Link key={rs} to={`/blog/${rs}`} className="bg-white border border-slate-200/60 rounded-xl p-4 hover:shadow-md hover:-translate-y-0.5 transition-all group">
                    <span className="text-xs font-medium text-cyan-700 bg-cyan-50 px-2 py-1 rounded-full">{related.category}</span>
                    <p className="text-sm font-semibold text-slate-800 mt-2 group-hover:text-cyan-700 transition-colors">{related.title}</p>
                  </Link>
                );
              })}
            </div>
          </section>
        )}

        {/* Back to blog */}
        <div className="text-center pt-6 border-t border-slate-200">
          <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-cyan-700 hover:underline font-medium">
            <ArrowLeft className="h-4 w-4" /> Alle Ratgeber-Artikel
          </Link>
        </div>
      </article>

      {/* Simple footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <p className="text-sm text-slate-400">&copy; {new Date().getFullYear()} Lehnen & Weinacht — Pflegeberatung & Alltagsbegleitung</p>
          <div className="flex justify-center gap-6 mt-3 text-sm text-slate-500">
            <Link to="/" className="hover:text-white transition-colors">Startseite</Link>
            <Link to="/blog" className="hover:text-white transition-colors">Ratgeber</Link>
            <a href="/#kontakt" className="hover:text-white transition-colors">Kontakt</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
